/**
 * Visual baseline review (Migration 061 / Delivery roadmap Tier 2.3).
 *
 * Enables an in-app "Approve / Reject" workflow for failed
 * `visual-check` steps. Each promotion overwrites the durable
 * baseline image referenced by future runs of the same step on
 * the same branch.
 *
 * Baseline-per-branch: a different baseline can exist for `main` vs
 * `release-2026-q3`, so a CSS refactor on a release branch doesn't
 * accidentally repaint the main-branch baseline.
 */
import { z } from 'zod';

/** "main" is the implicit default branch. Branch is free-form text
 *  to mirror Git refs but with our own length cap. */
export const VisualBaselineBranchSchema = z.string().min(1).max(120).default('main');

/** A single failed visual-check step lifted out of `test_runs.step_results`. */
export const VisualDiffRowSchema = z.object({
  runId: z.string().uuid(),
  stepId: z.string(),
  stepTitle: z.string(),
  /** 'mismatch' = below threshold; 'new_baseline' = first run, baseline auto-created. */
  status: z.enum(['mismatch', 'new_baseline']),
  matchPercentage: z.number().min(0).max(100),
  threshold: z.number().min(0).max(100),
  diffPixels: z.number().int().min(0),
  totalPixels: z.number().int().min(0),
  baselineUrl: z.string(),
  actualUrl: z.string(),
  diffUrl: z.string().nullable(),
  branch: z.string().default('main'),
  capturedAt: z.string(),
  /**
   * Resolved durable baseline id when a `visual_baselines` row exists
   * for this (flow, step, branch). Null for `new_baseline` rows where
   * no durable baseline has been written yet. Used by the painter UI
   * to target the right PATCH endpoint.
   */
  baselineId: z.string().uuid().nullable().optional(),
});
export type VisualDiffRow = z.infer<typeof VisualDiffRowSchema>;

export const ListVisualDiffsResponseSchema = z.object({
  runId: z.string().uuid(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  rows: z.array(VisualDiffRowSchema),
});
export type ListVisualDiffsResponse = z.infer<typeof ListVisualDiffsResponseSchema>;

/** Promote one (runId, stepId) into the durable baseline store. */
export const PromoteBaselineItemSchema = z.object({
  runId: z.string().uuid(),
  stepId: z.string().min(1),
  /** Defaults to 'main' when omitted. */
  branch: z.string().min(1).max(120).optional(),
});
export type PromoteBaselineItem = z.infer<typeof PromoteBaselineItemSchema>;

export const PromoteBaselineRequestSchema = z.object({
  items: z.array(PromoteBaselineItemSchema).min(1).max(200),
});
export type PromoteBaselineRequest = z.infer<typeof PromoteBaselineRequestSchema>;

export const PromoteBaselineResultSchema = z.object({
  runId: z.string().uuid(),
  stepId: z.string(),
  status: z.enum(['promoted', 'noop', 'error']),
  baselineId: z.string().uuid().nullable(),
  /** Populated on `error`. Wire-codes from RynoraErrorCode. */
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
});
export type PromoteBaselineResult = z.infer<typeof PromoteBaselineResultSchema>;

export const PromoteBaselineResponseSchema = z.object({
  results: z.array(PromoteBaselineResultSchema),
  promoted: z.number().int().min(0),
  noop: z.number().int().min(0),
  errors: z.number().int().min(0),
});
export type PromoteBaselineResponse = z.infer<typeof PromoteBaselineResponseSchema>;

/** Listing baselines under an org/project for the management screen. */
export const VisualBaselineRowSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  flowId: z.string().uuid(),
  flowName: z.string(),
  stepId: z.string(),
  stepTitle: z.string(),
  branch: z.string(),
  imageUrl: z.string(),
  imageSha256: z.string(),
  threshold: z.number().min(0).max(100),
  version: z.number().int().min(1),
  approvedBy: z.string().uuid().nullable(),
  approvedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type VisualBaselineRow = z.infer<typeof VisualBaselineRowSchema>;

export const ListBaselinesResponseSchema = z.object({
  rows: z.array(VisualBaselineRowSchema),
  totalCount: z.number().int().min(0),
});
export type ListBaselinesResponse = z.infer<typeof ListBaselinesResponseSchema>;

/**
 * Smart-region matching (Migration 099 / `pending-applitools-visual-ai`).
 *
 * A bounding-box painted by the operator on top of the baseline image.
 * Coordinates are in image pixels (not viewport CSS pixels) so the
 * region survives device-pixel-ratio differences between capture and
 * review machines.
 */
export const IgnoreRegionSchema = z.object({
  bbox: z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    width: z.number().int().min(1),
    height: z.number().int().min(1),
  }),
  reason: z.string().max(200).optional(),
});
export type IgnoreRegion = z.infer<typeof IgnoreRegionSchema>;

/**
 * PATCH body for the painter UI. Replaces `visual_baselines.ignore_regions`
 * wholesale (no merge semantics — the editor owns the full list).
 */
export const UpdateIgnoreRegionsRequestSchema = z.object({
  ignoreRegions: z.array(IgnoreRegionSchema).max(50),
});
export type UpdateIgnoreRegionsRequest = z.infer<typeof UpdateIgnoreRegionsRequestSchema>;

export const UpdateIgnoreRegionsResponseSchema = z.object({
  ok: z.literal(true),
  count: z.number().int().min(0),
});
export type UpdateIgnoreRegionsResponse = z.infer<typeof UpdateIgnoreRegionsResponseSchema>;

/**
 * Visual baseline identity for the first implementation slice.
 * Do NOT widen with browser/viewport/device here — that is a later product decision.
 */
export const VisualBaselineIdentitySchema = z.object({
  orgId: z.string().uuid(),
  flowId: z.string().uuid(),
  stepId: z.string().min(1),
  branch: z.string().min(1).max(120).default('main'),
});
export type VisualBaselineIdentity = z.infer<typeof VisualBaselineIdentitySchema>;

/** Durable row candidate before approved-only classification. */
export const VisualBaselineCandidateSchema = z.object({
  id: z.string().uuid(),
  stepId: z.string().min(1),
  branch: z.string().min(1).max(120),
  imageUrl: z.string(),
  imageSha256: z.string(),
  /** Null/absent means not approved — must FAIL for visual-check (no auto-seed). */
  approvedAt: z.string().nullable(),
  /**
   * Painter `visual_baselines.ignore_regions` raw JSON from the same baseline row.
   * Validated at visual-check (FAIL CLOSED). Omitted/undefined → empty Painter set.
   */
  ignoreRegions: z.unknown().optional(),
});
export type VisualBaselineCandidate = z.infer<typeof VisualBaselineCandidateSchema>;

export type VisualBaselineLookupClassification =
  | { status: 'missing' }
  | { status: 'not_approved'; baselineId: string }
  | { status: 'invalid'; reason: string; baselineId?: string }
  | {
      status: 'approved';
      baseline: {
        id: string;
        imageUrl: string;
        imageSha256: string;
        approvedAt: string;
      };
    };

function isHexSha256(value: string): boolean {
  return /^[a-fA-F0-9]{64}$/.test(value);
}

/**
 * Classify a durable baseline candidate for the approved-lookup gate.
 * Missing / not approved / invalid must never become PASS or auto-seed.
 */
export function classifyApprovedVisualBaseline(
  candidate:
    | {
        id: string;
        imageUrl: string;
        imageSha256: string;
        approvedAt: string | null;
      }
    | null
    | undefined,
): VisualBaselineLookupClassification {
  if (!candidate) return { status: 'missing' };
  if (!candidate.approvedAt || !String(candidate.approvedAt).trim()) {
    return { status: 'not_approved', baselineId: candidate.id };
  }
  const imageUrl = (candidate.imageUrl || '').trim();
  if (!imageUrl) {
    return { status: 'invalid', reason: 'empty image url', baselineId: candidate.id };
  }
  const sha = (candidate.imageSha256 || '').trim();
  if (!isHexSha256(sha)) {
    return { status: 'invalid', reason: 'invalid image sha256', baselineId: candidate.id };
  }
  return {
    status: 'approved',
    baseline: {
      id: candidate.id,
      imageUrl,
      imageSha256: sha.toLowerCase(),
      approvedAt: String(candidate.approvedAt).trim(),
    },
  };
}
